import { Component } from 'preact';
import { Localizer, Text } from 'preact-i18n';
import { connect } from 'unistore/preact';
import update from 'immutability-helper';
import BaseEditBox from '../baseEditBox';
import SelectDeviceFeature from '../../device/SelectDeviceFeature';
import { getDeviceFeatureName } from '../../../utils/device';
import { DeviceListWithDragAndDrop } from '../../drag-and-drop/DeviceListWithDragAndDrop';
import withIntlAsProp from '../../../utils/withIntlAsProp';

class EditDevices extends Component {
  addDeviceFeature = async (feature, device) => {
    // SelectDeviceFeature also reports a cleared selection with null
    if (!feature) {
      return;
    }
    const selectedDeviceFeatureOption = {
      value: feature.selector,
      label: getDeviceFeatureName(this.props.intl.dictionary, device, feature)
    };
    // The picker loads on its own, so it can be used even if the fetch of the
    // already picked features failed and left this list unset.
    const newSelectedDeviceFeaturesOptions = [
      ...(this.state.selectedDeviceFeaturesOptions || []),
      selectedDeviceFeatureOption
    ];
    await this.setState({ selectedDeviceFeaturesOptions: newSelectedDeviceFeaturesOptions });
    this.refreshDeviceFeaturesNames();
  };

  /*
   * Which feature governs a row, and at what value.
   *
   * Stored on the box rather than on the feature, because it is a statement
   * about this dashboard: the same pump may be a control on a maintenance
   * page and a reading on a wall panel. Cleared entirely when no governing
   * feature is chosen, so an unused rule never lingers in the config.
   */
  updateEnabledBy = (selector, patch) => {
    const current = { ...(this.props.box.feature_enabled_by || {}) };
    const rule = { ...(current[selector] || {}), ...patch };
    if (!rule.feature) {
      delete current[selector];
    } else {
      current[selector] = { feature: rule.feature, value: rule.value === undefined ? '' : `${rule.value}` };
    }
    this.props.updateBoxConfig(this.props.x, this.props.y, { feature_enabled_by: current });
  };

  /*
   * Every feature of this box, with the values it can hold — what the row
   * pickers offer. A select carries its own options; anything else is read as
   * a number, and for the binaries this is used with that is off and on.
   */
  governingFeatures = () => {
    const bySelector = new Map();
    (this.state.devices || []).forEach(device => {
      (device.features || []).forEach(feature => {
        bySelector.set(feature.selector, feature);
      });
    });
    return (this.state.selectedDeviceFeaturesOptions || []).map(option => {
      const feature = bySelector.get(option.value);
      const supported = feature && Array.isArray(feature.supported_options) ? feature.supported_options : [];
      const values = supported.length
        ? supported.map(o => ({ value: `${o.value}`, label: o.label || `${o.value}` }))
        : [
            { value: '0', label: '0' },
            { value: '1', label: '1' }
          ];
      return { selector: option.value, label: option.new_label || option.label, values };
    });
  };

  updateName = e => {
    this.props.updateBoxConfig(this.props.x, this.props.y, {
      name: e.target.value
    });
  };

  refreshDeviceFeaturesNames = () => {
    const newDeviceFeatureNames = this.state.selectedDeviceFeaturesOptions.map(o => {
      return o.new_label !== undefined && o.new_label !== '' ? o.new_label : o.label;
    });
    const newDeviceFeature = this.state.selectedDeviceFeaturesOptions.map(o => {
      return o.value;
    });
    this.props.updateBoxConfig(this.props.x, this.props.y, {
      device_feature_names: newDeviceFeatureNames,
      device_features: newDeviceFeature
    });
  };

  refreshDisplayForNewProps = async () => {
    if (!this.state.devices) {
      return;
    }
    if (!this.props.box || !this.props.box.device_features) {
      return;
    }
    const selectedDeviceFeaturesOptions = this.getSelectedDeviceFeaturesOptions(this.state.devices);
    await this.setState({ selectedDeviceFeaturesOptions });
  };

  updateDeviceFeatureName = async (index, name) => {
    const newState = update(this.state, {
      selectedDeviceFeaturesOptions: {
        [index]: {
          new_label: {
            $set: name
          }
        }
      }
    });
    await this.setState(newState);

    if (name !== '') {
      this.refreshDeviceFeaturesNames();
    }
  };

  // Builds the list of already picked features (with their custom names) from the
  // box config. The picker itself is SelectDeviceFeature, which loads its own options.
  getSelectedDeviceFeaturesOptions = devices => {
    const selectedDeviceFeaturesOptions = [];
    if (!this.props.box.device_features) {
      return selectedDeviceFeaturesOptions;
    }
    devices.forEach(device => {
      device.features.forEach(feature => {
        const featureIndex = this.props.box.device_features.indexOf(feature.selector);
        if (featureIndex === -1) {
          return;
        }
        const featureOption = {
          value: feature.selector,
          label: getDeviceFeatureName(this.props.intl.dictionary, device, feature)
        };
        // and there is a name associated to it
        if (this.props.box.device_feature_names && this.props.box.device_feature_names[featureIndex]) {
          featureOption.new_label = this.props.box.device_feature_names[featureIndex];
        }
        selectedDeviceFeaturesOptions.push(featureOption);
      });
    });
    return selectedDeviceFeaturesOptions.sort(
      (a, b) => this.props.box.device_features.indexOf(a.value) - this.props.box.device_features.indexOf(b.value)
    );
  };

  // Without the devices list, fall back to what the box already stores: the
  // picker loads on its own, and a pick must not write the list back without
  // the features that were saved before.
  getStoredDeviceFeaturesOptions = () => {
    const deviceFeatures = this.props.box.device_features || [];
    const deviceFeatureNames = this.props.box.device_feature_names || [];
    return deviceFeatures.map((selector, index) => {
      const option = { value: selector, label: deviceFeatureNames[index] || selector };
      // same shape as the resolved options: the list fills its name inputs from new_label
      if (deviceFeatureNames[index]) {
        option.new_label = deviceFeatureNames[index];
      }
      return option;
    });
  };

  getDeviceFeatures = async () => {
    try {
      this.setState({ loading: true });
      const devices = await this.props.httpClient.get(`/api/v1/device`);
      const selectedDeviceFeaturesOptions = this.getSelectedDeviceFeaturesOptions(devices);
      await this.setState({ devices, selectedDeviceFeaturesOptions, loading: false });
      this.refreshDeviceFeaturesNames();
    } catch (e) {
      console.error(e);
      this.setState({ selectedDeviceFeaturesOptions: this.getStoredDeviceFeaturesOptions(), loading: false });
    }
  };

  moveDevice = async (currentIndex, newIndex) => {
    const element = this.state.selectedDeviceFeaturesOptions[currentIndex];

    const newStateWithoutElement = update(this.state, {
      selectedDeviceFeaturesOptions: {
        $splice: [[currentIndex, 1]]
      }
    });
    const newState = update(newStateWithoutElement, {
      selectedDeviceFeaturesOptions: {
        $splice: [[newIndex, 0, element]]
      }
    });
    await this.setState(newState);
    this.refreshDeviceFeaturesNames();
  };

  removeDevice = async index => {
    const newStateWithoutElement = update(this.state, {
      selectedDeviceFeaturesOptions: {
        $splice: [[index, 1]]
      }
    });
    await this.setState(newStateWithoutElement);
    this.refreshDeviceFeaturesNames();
  };

  componentDidMount() {
    this.getDeviceFeatures();
  }

  componentDidUpdate(prevProps) {
    if (prevProps.box.device_features !== this.props.box.device_features) {
      this.refreshDisplayForNewProps();
    }
  }

  render(props, { selectedDeviceFeaturesOptions, loading }) {
    const selectedDeviceFeatures = (selectedDeviceFeaturesOptions || []).map(option => option.value);
    return (
      <BaseEditBox {...props} titleKey="dashboard.boxTitle.devices">
        <div class={loading ? 'dimmer active' : 'dimmer'}>
          <div class="loader" />
          <div class="dimmer-content">
            <div class="form-group">
              <label>
                <Text id="dashboard.boxes.devices.editNameLabel" />
              </label>
              <Localizer>
                <input
                  type="text"
                  class="form-control"
                  placeholder={<Text id="dashboard.boxes.devices.editNamePlaceholder" />}
                  value={props.box.name}
                  onInput={this.updateName}
                />
              </Localizer>
            </div>
            <div class="form-group">
              <label>
                <Text id="dashboard.boxes.devices.editDeviceFeaturesLabel" />
              </label>
              {selectedDeviceFeaturesOptions && (
                <DeviceListWithDragAndDrop
                  selectedDeviceFeaturesOptions={selectedDeviceFeaturesOptions}
                  moveDevice={this.moveDevice}
                  removeDevice={this.removeDevice}
                  updateDeviceFeatureName={this.updateDeviceFeatureName}
                  governingFeatures={this.governingFeatures()}
                  featureEnabledBy={props.box.feature_enabled_by || {}}
                  updateEnabledBy={this.updateEnabledBy}
                />
              )}
            </div>
            <div class="form-group">
              <label>
                <Text id="dashboard.boxes.devices.addADeviceLabel" />
              </label>
              {/* Grouped by room (same picker as scenes), so two devices sharing a
                  name stay distinguishable. No value: the picker resets after each add.
                  Mounted right away so its own fetches overlap the one above, under
                  the dimmer, instead of leaving a gap here once the dimmer lifts. */}
              <SelectDeviceFeature
                excludedDeviceFeatures={selectedDeviceFeatures}
                onDeviceFeatureChange={this.addDeviceFeature}
              />
            </div>
          </div>
        </div>
      </BaseEditBox>
    );
  }
}

export default withIntlAsProp(connect('httpClient', {})(EditDevices));
